import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http'
import { Observable, catchError, from, map, of, switchMap } from 'rxjs'
import { Injectable, inject } from '@angular/core'

import { AuthorizationDetail, AuthorizationStatus, CreditAuthorization, CreditHistoryQuery, CreditHistoryView, ReviewAuthorizationBody, TrayItem } from '@data/interfaces/credit-history.interface'
import { fileNameFromContentDisposition } from '@data/utils/content-disposition.util'
import { SnapshotResponse } from '@data/interfaces/verification-snapshot.interface'
import { environment } from '@env/environment'

const CONNECTION_ERROR = 'Hubo un error de conexión'

//* El campo del expediente donde vive el formato (catálogo de documentos de la API, «Formato revisar buró»).
export const AUTHORIZATION_DOCUMENT_FIELD = 'creditBureauAuthorization'

/** Lo que hace falta del cliente para subir un archivo a su expediente (lo mismo que arma `customer-documents`). */
export interface ExpedienteTarget {
  clientId: string
  rfc: string
  docsId: string
  documentType: string
}

export interface AuthorizationFile {
  blob: Blob
  fileName: string
}

/**
 * Historial crediticio (plan v4.2 §9, E5a y E5b).
 *
 *   GET  /clients/:id/credit-history                                · pestaña «Buró y Círculo»
 *   POST /clients/:id/credit-history/authorizations { documentUrl } · mandar a validación un formato del expediente
 *   GET  /credit-history/authorizations[?status=]                   · bandeja de validaciones
 *   GET  /credit-history/authorizations/:id                         · lo que necesita la validación
 *   POST /credit-history/authorizations/:id/review                  · aprobar o rechazar (solo Legal)
 *   GET  /credit-history/authorizations/:id/document                · el PDF (su copia), por proxy
 *   POST /clients/:id/credit-history/queries                         · consultar a Círculo (solo Legal)
 *
 * Como los demás servicios de la ficha: ningún método lanza; el error viaja en el sobre con `status` y `code`.
 */
@Injectable({ providedIn: 'root' })
export class ApiCreditHistoryService {
  private readonly http = inject(HttpClient)

  forClient(clientId: string): Observable<SnapshotResponse<CreditHistoryView>> {
    return this.wrap(this.http.get(`${environment.apiUrl}clients/${clientId}/credit-history`), 'No pudimos cargar el historial crediticio.')
  }

  /** Consulta a Círculo, o reutiliza una reciente. Tras un timeout, la API exige aceptar un posible segundo cargo. */
  query(clientId: string, acknowledgePossibleCharge = false): Observable<SnapshotResponse<CreditHistoryQuery>> {
    return this.wrap(
      this.http.post(`${environment.apiUrl}clients/${clientId}/credit-history/queries`, acknowledgePossibleCharge ? { acknowledgePossibleCharge: true } : {}),
      'No se pudo consultar a Círculo de Crédito.',
    )
  }

  sendToValidation(clientId: string, documentUrl: string): Observable<SnapshotResponse<CreditAuthorization>> {
    return this.wrap(this.http.post(`${environment.apiUrl}clients/${clientId}/credit-history/authorizations`, { documentUrl }), 'No se pudo mandar el formato a validación.')
  }

  /**
   * Sube el PDF al expediente del cliente (`POST /documents`, el que sí escribe en `client.docs`) y lo manda a
   * validación con la url que quedó guardada. Si la subida sale bien pero el envío no, el archivo queda en el
   * expediente y se puede mandar después desde la lista.
   */
  uploadAndSend(target: ExpedienteTarget, file: File): Observable<SnapshotResponse<CreditAuthorization>> {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('id', target.rfc)
    fd.append('documentName', AUTHORIZATION_DOCUMENT_FIELD)
    fd.append('folderName', 'holder')
    fd.append('docsId', target.docsId)
    fd.append('documentType', target.documentType)
    return this.wrap<any>(this.http.post(`${environment.apiUrl}documents`, fd), 'No se pudo subir el formato al expediente.').pipe(
      switchMap((uploaded) => {
        if (uploaded.error) return of(uploaded as SnapshotResponse<CreditAuthorization>)
        const files: any[] = uploaded.data?.holder?.[AUTHORIZATION_DOCUMENT_FIELD] ?? []
        const url = files.length ? files[files.length - 1]?.url : null
        if (!url) return of({ error: true, msg: 'El formato se subió, pero no encontramos el archivo en el expediente. Mándalo desde la lista.', data: null, status: 200 })
        return this.sendToValidation(target.clientId, url)
      }),
    )
  }

  tray(status: AuthorizationStatus = 'pending'): Observable<SnapshotResponse<{ items: TrayItem[] }>> {
    return this.wrap(this.http.get(`${environment.apiUrl}credit-history/authorizations`, { params: { status } }), 'No pudimos cargar la bandeja.')
  }

  detail(id: string): Observable<SnapshotResponse<AuthorizationDetail>> {
    return this.wrap(this.http.get(`${environment.apiUrl}credit-history/authorizations/${id}`), 'No pudimos cargar la autorización.')
  }

  review(id: string, body: ReviewAuthorizationBody): Observable<SnapshotResponse<CreditAuthorization>> {
    return this.wrap(this.http.post(`${environment.apiUrl}credit-history/authorizations/${id}/review`, body), 'No se pudo guardar la revisión.')
  }

  document(id: string): Observable<SnapshotResponse<AuthorizationFile>> {
    return this.http.get(`${environment.apiUrl}credit-history/authorizations/${id}/document`, { responseType: 'blob', observe: 'response' }).pipe(
      map((response: HttpResponse<Blob>): SnapshotResponse<AuthorizationFile> => {
        if (!response.body) return { error: true, msg: 'El servidor no entregó el PDF', data: null, status: response.status }
        const fileName = fileNameFromContentDisposition(response.headers.get('content-disposition')) ?? 'autorizacion-buro.pdf'
        return { error: false, msg: '', data: { blob: response.body, fileName }, status: response.status }
      }),
      catchError((e: HttpErrorResponse) => {
        //* Con `responseType: 'blob'` el cuerpo del error también llega como Blob: se lee para no perder el mensaje.
        const body$ =
          e?.error instanceof Blob
            ? from(e.error.text()).pipe(
                map((text) => JSON.parse(text)),
                catchError(() => of(null)),
              )
            : of(e?.error)
        return body$.pipe(map((body) => this.toError<AuthorizationFile>(e, 'No se pudo abrir el PDF', body)))
      }),
    )
  }

  private wrap<T>(source: Observable<unknown>, fallback: string): Observable<SnapshotResponse<T>> {
    return source.pipe(
      map((r: any) => ({ error: !!r?.error, msg: r?.msg ?? '', data: (r?.data ?? null) as T | null, status: 200 })),
      catchError((e: HttpErrorResponse) => of(this.toError<T>(e, fallback, e?.error))),
    )
  }

  private toError<T>(e: HttpErrorResponse, fallback: string, body: any): SnapshotResponse<T> {
    const status = e?.status ?? 0
    if (status === 0) return { error: true, msg: CONNECTION_ERROR, data: null, status, code: 'NETWORK' }
    //* El filtro de la API manda el motivo de negocio en `msg` (qué falta para aprobar, el formato repetido…).
    return { error: true, msg: body?.msg ?? fallback, data: null, status, code: body?.code ?? (status === 403 ? 'FORBIDDEN' : undefined) }
  }
}
